/**
 * What the keyboard and the wheel mean on a crag's model, kept apart from the viewer so
 * it can be tested without a GPU. Pans are in the terms of `OrbitControls.pan`: pixels
 * the camera moves, so a positive `y` is the camera going up and the rock going down.
 */
export interface ModelPan {
  x: number;
  y: number;
}

type WheelInput = Pick<WheelEvent, 'deltaX' | 'deltaY' | 'deltaMode' | 'shiftKey' | 'altKey' | 'ctrlKey' | 'metaKey'>;
type KeyInput = Pick<KeyboardEvent, 'key' | 'shiftKey' | 'altKey' | 'ctrlKey' | 'metaKey'>;

/** The pixels of a wheel's other two units, the same OrbitControls uses for its zoom. */
const PIXELS_PER_LINE = 16;
const PIXELS_PER_PAGE = 100;

/** An arrow's step as a share of the canvas's height, and how much longer with Shift. */
const ARROW_STEP = 0.05;
const ARROW_LONG_STEP = 4;

/** Everything that has a use of its own for an arrow or a letter. */
const OWNS_ITS_KEYS = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  ...['slider', 'textbox', 'spinbutton', 'combobox', 'listbox', 'option', 'menu', 'menuitem', 'radio', 'tab', 'dialog'].map(
    (role) => `[role="${role}"]`,
  ),
].join(',');

/**
 * The pan a wheel asks for, or null when it is the orbit's to handle.
 *
 * Shift reads `deltaX` first because the browser may have turned the wheel already:
 * Chrome and macOS move it to `deltaX` under Shift, Firefox leaves it on `deltaY`.
 * Ctrl is how a browser reports a pinch, so it stays a zoom.
 */
export function wheelPan(event: WheelInput): ModelPan | null {
  if (event.ctrlKey || event.metaKey || !(event.shiftKey || event.altKey)) return null;
  const unit = event.deltaMode === 1 ? PIXELS_PER_LINE : event.deltaMode === 2 ? PIXELS_PER_PAGE : 1;
  // Scrolling down shows what is further down, as on a page.
  if (event.shiftKey) return { x: -(event.deltaX || event.deltaY) * unit, y: 0 };
  return { x: 0, y: -event.deltaY * unit };
}

/** The pan an arrow asks for on a canvas this tall, or null when it is not one. */
export function arrowPan(event: KeyInput, canvasHeight: number): ModelPan | null {
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  const step = canvasHeight * ARROW_STEP * (event.shiftKey ? ARROW_LONG_STEP : 1);
  switch (event.key) {
    case 'ArrowUp':
      return { x: 0, y: step };
    case 'ArrowDown':
      return { x: 0, y: -step };
    case 'ArrowLeft':
      return { x: step, y: 0 };
    case 'ArrowRight':
      return { x: -step, y: 0 };
    default:
      return null;
  }
}

/** The key that makes the orbit turn around the rock under the pointer. */
export function isPivotKey(event: KeyInput) {
  return !event.altKey && !event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'f';
}

/** Whether a key press is free to mean something on the model, given what has the focus. */
export function keyboardIsFree(focused: Element | null) {
  return !focused?.closest(OWNS_ITS_KEYS);
}

/** ⌥ and ⇧ rather than Alt and Shift, and ⌘ rather than Ctrl. */
export const isMac = () => /mac/i.test(navigator.platform);
